import { useEffect, useState, type ChangeEvent, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";

import { ApiError } from "@/pages/auth/api";
import { getStoredSessionToken, getStoredUsername } from "@/pages/auth/storage";
import {
  getOwnProfile,
  updateOwnProfile,
  type UserPrivateProfile,
} from "@/pages/profile/api";
import { initialsForUsername } from "@/pages/profile/formatters";
import { useI18n } from "@/shared/i18n/LanguageContext";

type FormState = {
  avatar_url: string;
  fide_rating: string;
  title: string;
  china_title: string;
  uscf: string;
  cfc: string;
  ecf: string;
  dsb: string;
  rcf: string;
  bio: string;
};

const AVATAR_MAX_INPUT_BYTES = 8 * 1024 * 1024;
const AVATAR_MAX_DIMENSION = 384;
const AVATAR_JPEG_QUALITY = 0.84;

function toFormState(profile: UserPrivateProfile): FormState {
  return {
    avatar_url: profile.avatar_url ?? "",
    fide_rating: profile.fide_rating ? String(profile.fide_rating) : "",
    title: profile.title ?? "",
    china_title: profile.china_title ?? "",
    uscf: profile.federation_ratings.uscf ? String(profile.federation_ratings.uscf) : "",
    cfc: profile.federation_ratings.cfc ? String(profile.federation_ratings.cfc) : "",
    ecf: profile.federation_ratings.ecf ? String(profile.federation_ratings.ecf) : "",
    dsb: profile.federation_ratings.dsb ? String(profile.federation_ratings.dsb) : "",
    rcf: profile.federation_ratings.rcf ? String(profile.federation_ratings.rcf) : "",
    bio: profile.bio ?? "",
  };
}

function numberOrNull(value: string) {
  if (!value.trim()) {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function loadImage(url: string) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("failed to read image"));
    image.src = url;
  });
}

async function readFileAsAvatarDataUrl(file: File) {
  if (!file.type.startsWith("image/")) {
    throw new Error("choose an image file");
  }
  if (file.size > AVATAR_MAX_INPUT_BYTES) {
    throw new Error("image must be 8MB or smaller");
  }

  const objectUrl = URL.createObjectURL(file);
  try {
    const image = await loadImage(objectUrl);
    const scale = Math.min(
      1,
      AVATAR_MAX_DIMENSION / image.naturalWidth,
      AVATAR_MAX_DIMENSION / image.naturalHeight,
    );
    const width = Math.max(1, Math.round(image.naturalWidth * scale));
    const height = Math.max(1, Math.round(image.naturalHeight * scale));
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (!context) {
      throw new Error("failed to prepare image");
    }
    context.fillStyle = "#0a0d10";
    context.fillRect(0, 0, width, height);
    context.drawImage(image, 0, 0, width, height);
    return canvas.toDataURL("image/jpeg", AVATAR_JPEG_QUALITY);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

export function EditProfilePage() {
  const navigate = useNavigate();
  const { copy } = useI18n();
  const labels = copy.profile.edit;
  const [profile, setProfile] = useState<UserPrivateProfile | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const token = getStoredSessionToken();
    if (!token) {
      navigate("/login");
      return;
    }

    let cancelled = false;
    void getOwnProfile(token)
      .then((ownProfile) => {
        if (cancelled) {
          return;
        }
        setProfile(ownProfile);
        setForm(toFormState(ownProfile));
        setIsLoading(false);
      })
      .catch((loadError) => {
        if (cancelled) {
          return;
        }
        if (loadError instanceof ApiError && loadError.status === 401) {
          navigate("/login");
          return;
        }
        setError(loadError instanceof Error ? loadError.message : copy.profile.failed);
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [copy.profile.failed, navigate]);

  const onAvatarFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    try {
      const dataUrl = await readFileAsAvatarDataUrl(file);
      setForm((current) => (current ? { ...current, avatar_url: dataUrl } : current));
    } catch (fileError) {
      setError(fileError instanceof Error ? fileError.message : "failed to read image");
    }
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const token = getStoredSessionToken();
    if (!token || !form) {
      navigate("/login");
      return;
    }

    setError(null);
    setIsSaving(true);

    try {
      const updated = await updateOwnProfile(token, {
        avatar_url: form.avatar_url.trim() || null,
        fide_rating: numberOrNull(form.fide_rating),
        title: form.title.trim() || null,
        china_title: form.china_title.trim() || null,
        federation_ratings: {
          uscf: numberOrNull(form.uscf),
          cfc: numberOrNull(form.cfc),
          ecf: numberOrNull(form.ecf),
          dsb: numberOrNull(form.dsb),
          rcf: numberOrNull(form.rcf),
        },
        bio: form.bio.trim() || null,
      });
      setProfile(updated);
      setForm(toFormState(updated));
      navigate(`/@/${updated.username}`);
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : labels.failed);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading || !form) {
    return (
      <div className="page-stack">
        <section className="profile-edit-shell">
          <article className="profile-section">
            <p className="profile-status">{labels.loading}</p>
          </article>
        </section>
      </div>
    );
  }

  return (
    <div className="page-stack">
      <section className="profile-edit-shell">
        <form className="profile-edit-form" onSubmit={onSubmit}>
          <div className="profile-edit-form__head">
            {form.avatar_url ? (
              <img
                className="profile-edit-form__avatar-image"
                src={form.avatar_url}
                alt={profile?.username ?? "profile"}
              />
            ) : (
              <div className="profile-edit-form__avatar-fallback" aria-hidden="true">
                {initialsForUsername(profile?.username ?? getStoredUsername() ?? "me")}
              </div>
            )}

            <div className="stack">
              <p className="section-intro__eyebrow">{labels.eyebrow}</p>
              <h1 className="profile-name">@{profile?.username}</h1>
            </div>
          </div>

          <div className="profile-upload-row">
            <label className="button-link button-link--secondary" htmlFor="avatar-file">
              {labels.uploadPhoto}
            </label>
            <input
              id="avatar-file"
              className="profile-upload-input"
              type="file"
              accept="image/*"
              onChange={onAvatarFileChange}
            />
          </div>

          <div className="field-grid">
            <div className="field">
              <label htmlFor="profile-fide">{labels.fideRating}</label>
              <input
                id="profile-fide"
                type="number"
                value={form.fide_rating}
                onChange={(event) =>
                  setForm((current) =>
                    current ? { ...current, fide_rating: event.target.value } : current,
                  )
                }
              />
            </div>

            <div className="field">
              <label htmlFor="profile-title">{labels.title}</label>
              <input
                id="profile-title"
                type="text"
                value={form.title}
                onChange={(event) =>
                  setForm((current) =>
                    current ? { ...current, title: event.target.value } : current,
                  )
                }
              />
            </div>

            <div className="field">
              <label htmlFor="profile-china-title">{labels.chinaTitle}</label>
              <input
                id="profile-china-title"
                type="text"
                value={form.china_title}
                onChange={(event) =>
                  setForm((current) =>
                    current ? { ...current, china_title: event.target.value } : current,
                  )
                }
              />
            </div>

            <div className="field">
              <label htmlFor="profile-uscf">USCF</label>
              <input
                id="profile-uscf"
                type="number"
                value={form.uscf}
                onChange={(event) =>
                  setForm((current) =>
                    current ? { ...current, uscf: event.target.value } : current,
                  )
                }
              />
            </div>

            <div className="field">
              <label htmlFor="profile-cfc">CFC</label>
              <input
                id="profile-cfc"
                type="number"
                value={form.cfc}
                onChange={(event) =>
                  setForm((current) =>
                    current ? { ...current, cfc: event.target.value } : current,
                  )
                }
              />
            </div>

            <div className="field">
              <label htmlFor="profile-ecf">ECF</label>
              <input
                id="profile-ecf"
                type="number"
                value={form.ecf}
                onChange={(event) =>
                  setForm((current) =>
                    current ? { ...current, ecf: event.target.value } : current,
                  )
                }
              />
            </div>

            <div className="field">
              <label htmlFor="profile-dsb">DSB</label>
              <input
                id="profile-dsb"
                type="number"
                value={form.dsb}
                onChange={(event) =>
                  setForm((current) =>
                    current ? { ...current, dsb: event.target.value } : current,
                  )
                }
              />
            </div>

            <div className="field">
              <label htmlFor="profile-rcf">RCF</label>
              <input
                id="profile-rcf"
                type="number"
                value={form.rcf}
                onChange={(event) =>
                  setForm((current) =>
                    current ? { ...current, rcf: event.target.value } : current,
                  )
                }
              />
            </div>
          </div>

          <div className="field">
            <label htmlFor="profile-bio">{labels.bio}</label>
            <textarea
              id="profile-bio"
              value={form.bio}
              onChange={(event) =>
                setForm((current) =>
                  current ? { ...current, bio: event.target.value } : current,
                )
              }
            />
          </div>

          {error ? <p className="auth-panel__error">{error}</p> : null}

          <div className="button-row">
            <button className="button-link button-link--primary" type="submit" disabled={isSaving}>
              {isSaving ? labels.saving : labels.save}
            </button>
            <button
              className="button-link button-link--secondary"
              type="button"
              onClick={() => navigate(`/@/${profile?.username}`)}
            >
              {labels.cancel}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}
